import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import { Button, ButtonRouterLink, Card, Spinner } from '../components/primitives';
import { supabase } from '../lib/supabase';
import './Invite.css';

type View =
  | 'resolving'
  | 'invalid'
  | 'mismatch'
  | 'accepting'
  | 'accepted';

const RESOLVING_MIN_MS = 250;
const ACCEPTED_REDIRECT_MS = 600;

export function Invite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [view, setView] = useState<View>('resolving');
  const [orgName, setOrgName] = useState<string>('');
  const [invitedEmail, setInvitedEmail] = useState<string>('');
  const [sessionEmail, setSessionEmail] = useState<string>('');

  // One-shot guard so React StrictMode (or rapid re-renders) can't double-fire
  // the accept_invitation RPC.
  const acceptedOnce = useRef(false);

  const showMismatch = useCallback(
    (invEmail: string, sessEmail: string) => {
      setInvitedEmail(invEmail);
      setSessionEmail(sessEmail);
      setView('mismatch');
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    function settle(next: () => void) {
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, RESOLVING_MIN_MS - elapsed);
      window.setTimeout(() => {
        if (!cancelled) next();
      }, wait);
    }

    async function run() {
      if (!token) {
        settle(() => setView('invalid'));
        return;
      }

      const [{ data: sessionData }, { data: resolved, error: resolveError }] =
        await Promise.all([
          supabase.auth.getSession(),
          supabase.rpc('resolve_invitation', { p_token: token })
        ]);
      if (cancelled) return;

      const row = Array.isArray(resolved) ? resolved[0] : null;
      if (resolveError || !row || !row.ok) {
        settle(() => setView('invalid'));
        return;
      }

      const invEmail = (row.email ?? '').toString();
      const orgN = (row.org_name ?? '').toString();
      const session = sessionData.session;

      if (session) {
        const sEmail = session.user.email ?? '';
        const match = sEmail.toLowerCase() === invEmail.toLowerCase();
        if (!match) {
          settle(() => showMismatch(invEmail, sEmail));
          return;
        }

        // Signed-in matching → auto-accept.
        settle(async () => {
          if (acceptedOnce.current) return;
          acceptedOnce.current = true;
          setOrgName(orgN);
          setView('accepting');
          const { data: acceptData, error: acceptError } = await supabase.rpc(
            'accept_invitation',
            { p_token: token }
          );
          if (cancelled) return;
          if (acceptError) {
            // Race: another tab consumed the token between resolve + accept.
            setView('invalid');
            return;
          }
          const acceptedRow =
            Array.isArray(acceptData) && acceptData[0] ? acceptData[0] : null;
          const acceptedOrg = (acceptedRow?.org_name as string | undefined) ?? orgN;
          const acceptedOrgId = (acceptedRow?.org_id as string | undefined) ?? null;
          setOrgName(acceptedOrg || orgN);
          setView('accepted');
          // Hand the just-accepted org to the shell. It picks up the hint on
          // mount, lands the user in this org, and clears the key.
          if (acceptedOrgId) {
            try {
              window.sessionStorage.setItem('pn:active-org-hint', acceptedOrgId);
            } catch {
              /* sessionStorage unavailable — shell falls back to oldest membership */
            }
          }
          window.setTimeout(() => {
            if (cancelled) return;
            navigate('/dashboard', { replace: true });
          }, ACCEPTED_REDIRECT_MS);
        });
        return;
      }

      // Signed-out: decide where to redirect based on account existence.
      const { data: existsData } = await supabase.rpc('invite_user_exists', {
        p_token: token
      });
      if (cancelled) return;
      const exists = !!existsData;
      const dest = exists ? '/sign-in' : '/sign-up';
      const search = `?invite=${encodeURIComponent(token)}`;
      settle(() => navigate(`${dest}${search}`, { replace: true }));
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, showMismatch]);

  async function handleSignOutAndContinue() {
    if (!token) return;
    await supabase.auth.signOut();
    navigate(`/sign-in?invite=${encodeURIComponent(token)}`, { replace: true });
  }

  return (
    <>
      <AuthChrome pageLabel="Invitation" />
      <main className="iv-main">
        <Card
          className="iv-card"
          aria-labelledby="iv-heading"
          data-invite-view={view}
        >
          {view === 'resolving' ? (
            <div className="iv-resolving" role="status">
              <Spinner size="sm" label="Checking your invitation" />
              <span className="iv-resolving-caption">
                Checking your invitation…
              </span>
            </div>
          ) : null}

          {view === 'invalid' ? (
            <>
              <h1 id="iv-heading" className="iv-heading">
                This invite isn't valid
              </h1>
              <p className="iv-lede">
                It may have expired, been revoked, or already been used.
              </p>
              <div className="iv-actions">
                <ButtonRouterLink to="/sign-in" variant="primary" size="md">
                  Go to sign in
                </ButtonRouterLink>
              </div>
            </>
          ) : null}

          {view === 'mismatch' ? (
            <>
              <h1 id="iv-heading" className="iv-heading">
                This invite is for someone else
              </h1>
              <p className="iv-lede">
                It was sent to <span className="iv-emph">{invitedEmail}</span>,
                but you're signed in as{' '}
                <span className="iv-emph">{sessionEmail}</span>. Sign in as the
                invited address to accept this invitation.
              </p>
              <div className="iv-actions">
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSignOutAndContinue}
                >
                  Sign out and continue
                </Button>
                <ButtonRouterLink
                  to="/dashboard"
                  variant="ghost"
                  size="md"
                >
                  Stay signed in as me
                </ButtonRouterLink>
              </div>
            </>
          ) : null}

          {view === 'accepting' ? (
            <>
              <h1 id="iv-heading" className="iv-heading">
                Joining {orgName}…
              </h1>
              <div className="iv-resolving" role="status">
                <Spinner size="sm" label="Joining" />
                <span className="iv-resolving-caption">
                  Adding you to the org and switching your active workspace.
                </span>
              </div>
            </>
          ) : null}

          {view === 'accepted' ? (
            <>
              <h1 id="iv-heading" className="iv-heading">
                Welcome to {orgName}
              </h1>
              <p className="iv-lede">
                You're a member now. Taking you to your dashboard…
              </p>
            </>
          ) : null}
        </Card>
      </main>
    </>
  );
}
